import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { AtButton } from "./AtButton";
import {
  BlockAutocompleteDropdown,
  getFilteredBlockCount,
  getFilteredBlocks,
  resolveBlockTitle,
} from "./BlockAutocompleteDropdown";
import { ChatLexicalEditor, ChatLexicalEditorHandle, MentionState } from "./ChatLexicalEditor";

export interface ChatInputHandle {
  insertBlockChip: (blockId: string, blockTitle: string, blockType: string) => void;
  replaceMentionWithChip: (blockId: string, blockTitle: string, blockType: string) => void;
  getBlockMentions: () => Array<{ blockId: string; blockTitle: string; blockType: string }>;
  setText: (text: string) => void;
}

export interface ChatInputProps {
  value: string;
  isThinking: boolean;
  isOpen: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  showStop?: boolean;
  isStopDisabled?: boolean;
  onStop?: () => void;
  onWillChangeHeight?: () => boolean;
  onDidChangeHeight?: (wasAtBottom: boolean) => void;
  availableBlocks?: WebDashboardContentSpecBlocksInner[];
  onBlockSelected?: (blockId: string, blockTitle: string, blockType: string) => void;
  onPickerActivate?: () => void;
  onPickerDeactivate?: () => void;
  targetDocument?: Document;
  onBlockContextsChange?: (
    contexts: Array<{ blockId: string; blockTitle: string; blockType: string }>
  ) => void;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      value,
      isThinking,
      isOpen,
      disabled = false,
      disabledMessage,
      onChange,
      onSubmit,
      showStop = false,
      isStopDisabled = false,
      onStop,
      onWillChangeHeight,
      onDidChangeHeight,
      availableBlocks,
      onBlockSelected,
      onPickerActivate,
      onPickerDeactivate,
      targetDocument: ownerDocument = document,
      onBlockContextsChange,
    },
    ref
  ) => {
    const isInputDisabled = isThinking || disabled;
    const shouldShowStopButton = Boolean(showStop && onStop);

    const [mentionActive, setMentionActive] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [pickerOpenViaButton, setPickerOpenViaButton] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const mentionActiveRef = useRef(false);
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const savedMentionRef = useRef<{
      nodeKey: string;
      atOffset: number;
      cursorOffset: number;
    } | null>(null);
    const lexicalRef = useRef<ChatLexicalEditorHandle>(null);

    const closeDropdown = useCallback(() => {
      mentionActiveRef.current = false;
      savedMentionRef.current = null;
      setMentionActive(false);
      setMentionQuery("");
      setPickerOpenViaButton(false);
      onPickerDeactivate?.();
    }, [onPickerDeactivate]);

    useImperativeHandle(ref, () => ({
      insertBlockChip(blockId: string, blockTitle: string, blockType: string) {
        lexicalRef.current?.insertBlockChip(blockId, blockTitle, blockType);
      },
      replaceMentionWithChip(blockId: string, blockTitle: string, blockType: string) {
        lexicalRef.current?.replaceMentionWithChip(blockId, blockTitle, blockType);
        closeDropdown();
      },
      getBlockMentions() {
        return lexicalRef.current?.getBlockMentions() ?? [];
      },
      setText(text: string) {
        lexicalRef.current?.setText(text);
      },
    }));

    const handleMentionStateChange = useCallback(
      (state: MentionState) => {
        const wasActive = mentionActiveRef.current;
        mentionActiveRef.current = state.active || pickerOpenViaButton;
        setMentionActive(state.active);
        setMentionQuery(state.query);
        if (state.active) {
          if (
            state.nodeKey !== undefined &&
            state.atOffset !== undefined &&
            state.cursorOffset !== undefined
          ) {
            savedMentionRef.current = {
              nodeKey: state.nodeKey,
              atOffset: state.atOffset,
              cursorOffset: state.cursorOffset,
            };
          }
          if (!wasActive) {
            setHighlightedIndex(0);
            onPickerActivate?.();
          }
          // Close button-triggered picker when typing @ takes over
          if (pickerOpenViaButton) {
            setPickerOpenViaButton(false);
          }
        } else {
          savedMentionRef.current = null;
          if (wasActive) {
            onPickerDeactivate?.();
          }
        }
      },
      [onPickerActivate, onPickerDeactivate, pickerOpenViaButton]
    );

    const handleMentionSelect = useCallback(
      (blockId: string, blockTitle: string, blockType: string) => {
        lexicalRef.current?.replaceMentionWithChip(blockId, blockTitle, blockType);
        closeDropdown();
      },
      [closeDropdown]
    );

    const handlePickerSelect = useCallback(
      (blockId: string, blockTitle: string, blockType: string) => {
        lexicalRef.current?.insertBlockChip(blockId, blockTitle, blockType);
        setPickerOpenViaButton(false);
      },
      []
    );

    const handleAtButtonClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (pickerOpenViaButton) {
          closeDropdown();
        } else {
          setPickerOpenViaButton(true);
          setHighlightedIndex(0);
          lexicalRef.current?.focus();
        }
      },
      [closeDropdown, pickerOpenViaButton]
    );

    const handlePickerClose = useCallback(() => {
      closeDropdown();
    }, [closeDropdown]);

    useEffect(() => {
      mentionActiveRef.current = mentionActive || pickerOpenViaButton;
    }, [mentionActive, pickerOpenViaButton]);

    const showDropdown = mentionActive || pickerOpenViaButton;
    const dropdownFilterText = mentionActive ? mentionQuery : "";
    const dropdownOnSelect = mentionActive ? handleMentionSelect : handlePickerSelect;

    useEffect(() => {
      if (!showDropdown) {
        return;
      }
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          closeDropdown();
        }
      };
      // Let dashboard-card click handlers run before closing the active block picker.
      ownerDocument.addEventListener("click", handleClickOutside);
      return () => ownerDocument.removeEventListener("click", handleClickOutside);
    }, [showDropdown, ownerDocument, closeDropdown]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (!showDropdown || !availableBlocks) {
          return;
        }

        const count = getFilteredBlockCount(availableBlocks, dropdownFilterText);

        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightedIndex(prev => (prev + 1) % Math.max(1, count));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightedIndex(prev => (prev - 1 + Math.max(1, count)) % Math.max(1, count));
          return;
        }
        if (e.key === "Enter") {
          const filtered = getFilteredBlocks(availableBlocks, dropdownFilterText);
          if (filtered.length > 0 && highlightedIndex < filtered.length) {
            e.preventDefault();
            e.stopPropagation();
            const block = filtered[highlightedIndex];
            dropdownOnSelect(block.block_id, resolveBlockTitle(block), block.type);
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          closeDropdown();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          closeDropdown();
        }
      },
      [
        showDropdown,
        availableBlocks,
        dropdownFilterText,
        highlightedIndex,
        dropdownOnSelect,
        closeDropdown,
      ]
    );

    const handleFormSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        closeDropdown();
        onSubmit(e);
      },
      [closeDropdown, onSubmit]
    );

    const defaultPlaceholder = "Ask AI";

    if (disabledMessage) {
      return (
        <div className="px-4 pb-3 pt-2 bg-white">
          <div className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-500 text-center">
            {disabledMessage}
          </div>
        </div>
      );
    }

    const hasBlocks = availableBlocks && availableBlocks.length > 0;

    return (
      <div className="px-3 pb-3 pt-0 bg-white">
        <form
          id="chatForm"
          onSubmit={handleFormSubmit}
          className="relative"
          onKeyDownCapture={handleKeyDown}
        >
          {showDropdown && availableBlocks && (
            <BlockAutocompleteDropdown
              blocks={availableBlocks}
              filterText={dropdownFilterText}
              onSelect={dropdownOnSelect}
              highlightedIndex={highlightedIndex}
              onHighlightedIndexChange={setHighlightedIndex}
              showHeader={pickerOpenViaButton}
              onClose={handlePickerClose}
            />
          )}
          <div
            ref={containerRef}
            className="flex max-h-[260px] flex-col overflow-hidden rounded-xl border-2 border-[#ddd] bg-[#fafafa] transition-colors focus-within:border-blue-500 focus-within:ring focus-within:ring-blue-500"
          >
            <ChatLexicalEditor
              ref={lexicalRef}
              disabled={isInputDisabled}
              isOpen={isOpen}
              value={value}
              placeholder={defaultPlaceholder}
              onChange={onChange}
              onSubmit={onSubmit}
              onMentionStateChange={handleMentionStateChange}
              onBlockSelected={onBlockSelected}
              onBlockContextsChange={onBlockContextsChange}
              availableBlocks={availableBlocks}
              onWillChangeHeight={onWillChangeHeight}
              onDidChangeHeight={onDidChangeHeight}
              mentionActiveRef={mentionActiveRef}
              savedMentionRef={savedMentionRef}
              blurTimeoutRef={blurTimeoutRef}
            />

            <div className="relative flex shrink-0 items-center justify-between px-3 pb-3">
              <AtButton
                active={pickerOpenViaButton}
                disabled={!hasBlocks}
                onMouseDown={handleAtButtonClick}
              />

              <button
                type={shouldShowStopButton ? "button" : "submit"}
                className={
                  "w-7 h-7 rounded-full text-white flex items-center justify-center transition-colors " +
                  "cursor-pointer disabled:cursor-not-allowed " +
                  (shouldShowStopButton
                    ? "bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:opacity-50"
                    : value.trim()
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-400 opacity-50 cursor-not-allowed")
                }
                aria-label={shouldShowStopButton ? "Stop" : "Send message"}
                disabled={shouldShowStopButton ? isStopDisabled : isInputDisabled || !value.trim()}
                onClick={
                  shouldShowStopButton
                    ? e => {
                        e.preventDefault();
                        onStop?.();
                      }
                    : undefined
                }
              >
                <span className="material-icons !text-lg">
                  {shouldShowStopButton ? "stop" : "arrow_upward"}
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }
);

ChatInput.displayName = "ChatInput";
